import { NgModule, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ChunkErrorHandler } from './shared/services/chunk-error-handler.service';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { LayoutModule } from './modules/layout/layout.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { ToastrModule } from 'ngx-toastr';
import { SizesGuideComponent } from './static/sizes-guide/sizes-guide.component';
import { CheckoutComponent } from './static/checkout/checkout.component';
import { StepperHeaderComponent } from './static/checkout/stepper/stepper-header/stepper-header.component';
import { StepperContainerComponent } from './static/checkout/stepper/stepper-container/stepper-container.component';
import { ShoppingCartComponent } from './static/checkout/stepper/steps/shopping-cart/shopping-cart.component';
import { BillingStepComponent } from './static/checkout/stepper/steps/billing-step/billing-step.component';
import { LoginGuestStepComponent } from './static/checkout/stepper/steps/login-guest-step/login-guest-step.component';
import { OrderSummaryStepComponent } from './static/checkout/stepper/steps/order-summary-step/order-summary-step.component';
import { StepperNavigationComponent } from './static/checkout/stepper/stepper-navigation/stepper-navigation.component';
import { SharedModule } from './modules/shared/shared.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthInterceptor } from './shared/interceptors/auth.interceptor';
import { CacheInterceptor } from './shared/interceptors/cache.interceptor';
import { CookieConsentComponent } from './shared/components/cookie-consent/cookie-consent.component';
import { CheckoutSuccessComponent } from './static/checkout/success/checkout-success.component';
import { NavigationTabsApiService } from './shared/services/navigation-tabs-api.service';
import { SettingsService } from './shared/services/settings.service';
import { LanguageInterceptor } from './shared/interceptors/language.interceptor';
import { lastValueFrom, take } from 'rxjs';

export function prefetchNavigationTabs(tabsService: NavigationTabsApiService) {
  return () =>
    lastValueFrom(
      tabsService
        .getActiveNavigationTabs()
        .pipe(take(1))
    ).catch(() => undefined);
}

export function prefetchHeroBanners(settingsService: SettingsService) {
  return () =>
    lastValueFrom(
      settingsService
        .getHeroBanners(1, 10)
        .pipe(take(1))
    ).catch(() => undefined);
}

@NgModule({
  declarations: [
    AppComponent,
    SizesGuideComponent,
    CheckoutComponent,
    CheckoutSuccessComponent,
    StepperHeaderComponent,
    StepperContainerComponent,
    ShoppingCartComponent,
    BillingStepComponent,
    LoginGuestStepComponent,
    OrderSummaryStepComponent,
    StepperNavigationComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    LayoutModule,
    RouterModule,
    CookieConsentComponent,
    ToastrModule.forRoot({
      timeOut: 5000,
      extendedTimeOut: 2000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      resetTimeoutOnDuplicate: true,
      closeButton: true,
      progressBar: true,
      enableHtml: true,
      tapToDismiss: true,
      toastClass: 'ngx-toastr custom-toast',
      titleClass: 'toast-title',
      messageClass: 'toast-message',
      iconClasses: {
        error: 'toast-error',
        info: 'toast-info', 
        success: 'toast-success',
        warning: 'toast-warning'
      }
    }),
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: prefetchNavigationTabs,
      deps: [NavigationTabsApiService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: prefetchHeroBanners,
      deps: [SettingsService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CacheInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: ChunkErrorHandler
    }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
